import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Table,
  Badge,
  ProgressBar,
  Offcanvas,
  Spinner,
} from "react-bootstrap";
import {
  FaCog,
  FaKey,
  FaPlus,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaSearch,
  FaEdit,
} from "react-icons/fa";
import {
  MdSecurity,
  MdVpnKey,
  MdRefresh,
  MdContentCopy,
  MdDelete,
  MdModeEditOutline,
} from "react-icons/md";
import {
  createLicense,
  getLicenses,
  deleteLicense,
  updateLicense,
  changeLicenseStatus,
  getLicenseUsage,
} from "../../redux/actions/settingActions";
import Pagination from "../../../helper/Pagination";
import DeleteConfirmModal from "./DeleteConfirmModal";
import StatusConfirmModal from "./StatusConfirmModal";
import LicenseUsageModal from "./LicenseUsageModal";

const Setting = () => {
  const dispatch = useDispatch();
  const {
    licenses = [],
    loading = false,
    error = null,
    pagination = {},
    createStatus = null,
    deleteStatus = null,
    updateStatus = null,
    statusChangeStatus = null,
    licenseUsage = [],
    usageLoading = false,
    usagePagination = {},
    usageStats = {},
  } = useSelector((state) => state.setting || {});
  const [showKeys, setShowKeys] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingLicense, setEditingLicense] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const NO_PARTNER_TOAST_ID = "no-partner-toast";
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    license: null,
    newStatus: null,
  });
  const [licenseUsageOffcanvas, setLicenseUsageOffcanvas] = useState({
    isOpen: false,
    license: null,
  });
  const [pendingLicense, setPendingLicense] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    reset: resetEdit,
    setValue: setValueEdit,
    formState: { errors: errorsEdit },
  } = useForm();

  useEffect(() => {
    dispatch(
      getLicenses({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
      })
    );
  }, [dispatch, currentPage, itemsPerPage, searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (value.length === 0 || (value.length > 0 && value[0] !== " ")) {
      setSearchTerm(value);
      setCurrentPage(1);
    } else if (searchTerm.length > 0) {
      setSearchTerm(value);
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (createStatus === 201) {
      reset();
      setShowCreateForm(false);
      dispatch(
        getLicenses({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        })
      );
    }
  }, [createStatus, dispatch, currentPage, itemsPerPage, searchTerm, reset]);

  useEffect(() => {
    if (deleteStatus === 200) {
      dispatch(
        getLicenses({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        })
      );
    }
  }, [deleteStatus, dispatch, currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    if (updateStatus === 200) {
      resetEdit();
      setShowEditForm(false);
      setEditingLicense(null);
      dispatch(
        getLicenses({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        })
      );
    }
  }, [
    updateStatus,
    dispatch,
    currentPage,
    itemsPerPage,
    searchTerm,
    resetEdit,
  ]);

  useEffect(() => {
    if (statusChangeStatus === 200) {
      dispatch(
        getLicenses({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        })
      );
    }
  }, [statusChangeStatus, dispatch, currentPage, itemsPerPage, searchTerm]);

  // Adjust current page if it exceeds total pages after data loads
  useEffect(() => {
    if (
      pagination.totalPages &&
      currentPage > pagination.totalPages &&
      pagination.totalPages > 0
    ) {
      setCurrentPage(pagination.totalPages);
    }
  }, [pagination.totalPages, currentPage]);

  const onSubmit = (data) => {
    const licenseData = {
      name: data.licenseName,
      licenseKey: generateLicenseKey(),
    };
    dispatch(createLicense(licenseData));
  };

  const onUpdateSubmit = (data) => {
    const updateData = {
      licenseId: editingLicense._id,
      name: data.editLicenseName,
    };
    dispatch(updateLicense(updateData));
  };

  const handleEditLicense = (license) => {
    setEditingLicense(license);
    setValueEdit("editLicenseName", license.name);
    setShowEditForm(true);
  };

  const cancelEdit = () => {
    setShowEditForm(false);
    setEditingLicense(null);
    resetEdit();
  };

  const handleStatusChange = (license) => {
    const newStatus = license.status === "active" ? "inactive" : "active";
    setStatusModal({ isOpen: true, license, newStatus });
  };

  const handleStatusConfirm = () => {
    dispatch(
      changeLicenseStatus({
        licenseId: statusModal.license._id,
        status: statusModal.newStatus,
      })
    );
    setStatusModal({ isOpen: false, license: null, newStatus: null });
  };

  const handleStatusCancel = () => {
    setStatusModal({ isOpen: false, license: null, newStatus: null });
  };

  const toggleKeyVisibility = (id) => {
    setShowKeys((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text, licenseId) => {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      window.isSecureContext
    ) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopiedId(licenseId);
          setTimeout(() => setCopiedId(null), 2000);
        })
        .catch(() => {
          // fallback
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          setCopiedId(licenseId);
          setTimeout(() => setCopiedId(null), 2000);
        });
    } else {
      // fallback for http
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedId(licenseId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleDeleteLicense = (license) => {
    setSelectedLicense(license);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedLicense) {
      dispatch(deleteLicense(selectedLicense._id));
      setShowDeleteModal(false);
      setSelectedLicense(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedLicense(null);
  };

  const maskKey = (key) => {
    if (!key || key.length < 50) return key || "••••••••••••";
    return key.substring(0, 10) + "•".repeat(39) + key.substring(49);
  };

  const generateLicenseKey = () => {
    const digits = "0123456789";
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    let characters = [];

    // Generate 30 digits
    for (let i = 0; i < 30; i++) {
      characters.push(digits.charAt(Math.floor(Math.random() * digits.length)));
    }

    // Generate 20 letters
    for (let i = 0; i < 20; i++) {
      characters.push(chars.charAt(Math.floor(Math.random() * chars.length)));
    }

    // Shuffle the array to mix digits and letters
    for (let i = characters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [characters[i], characters[j]] = [characters[j], characters[i]];
    }

    return characters.join("");
  };

  const handleTableRowClick = (license) => {
    if (!license.itsalesPartner?.id) {
      if (!toast.isActive(NO_PARTNER_TOAST_ID)) {
        toast.warning("No IT Sales Partner associated with this license", {
          toastId: NO_PARTNER_TOAST_ID,
        });
      }
      return;
    }

    setPendingLicense(license);
    const params = {
      licenseId: license._id,
      page: 1,
      limit: 10,
      search: "",
      itsalesId: license.itsalesPartner.id,
    };

    dispatch(getLicenseUsage(params));
  };

  useEffect(() => {
    if (pendingLicense && !usageLoading) {
      if (!error || error?.status !== 404) {
        setLicenseUsageOffcanvas({
          isOpen: true,
          license: pendingLicense,
        });
      }
      setPendingLicense(null);
    }
  }, [pendingLicense, usageLoading, error]);

  const handleUsageOffcanvasClose = () => {
    setLicenseUsageOffcanvas({
      isOpen: false,
      license: null,
    });
  };

  const totalLicenses = Array.isArray(licenses) ? licenses.length : 0;
  const activeLicenses = Array.isArray(licenses)
    ? licenses.filter((l) => l.status === "active").length
    : 0;
  const deactiveLicenses = Array.isArray(licenses)
    ? licenses.filter((l) => l.status === "inactive").length
    : 0;
  const totalUsage =
    Array.isArray(licenses) && licenses.length > 0
      ? Math.round(
          licenses.reduce((sum, l) => sum + (l.usage || 0), 0) / licenses.length
        )
      : 0;

  return (
    <>
      <div className="page-container">
        <div className="page-card scroll-container">
          <div className="form-header-AllCoupon d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">
                <FaCog className="me-2 text-primary" />
                License Management
              </h5>
              <p className="text-muted mb-0">
                Manage license keys and Expiration
              </p>
            </div>
            <div className="d-flex gap-3">
              <div className="stats-card">
                <div className="stats-icon bg-primary">
                  <MdVpnKey />
                </div>
                <div>
                  <div className="stats-number">{totalLicenses}</div>
                  <div className="stats-label">Per Page Licenses</div>
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-icon bg-success">
                  <MdSecurity />
                </div>
                <div>
                  <div className="stats-number">{activeLicenses}</div>
                  <div className="stats-label">Active</div>
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-icon bg-success">
                  <MdSecurity />
                </div>
                <div>
                  <div className="stats-number">{deactiveLicenses}</div>
                  <div className="stats-label">Deactive</div>
                </div>
              </div>
              {/* <div className="stats-card">
                <div className="stats-icon bg-info">
                  <MdRefresh />
                </div>
                <div>
                  <div className="stats-number">{totalUsage}%</div>
                  <div className="stats-label">Avg Usage</div>
                </div>
              </div> */}
            </div>
          </div>

          <div className="table-section">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0">License Keys :</h6>
                <span className="text-dark">
                  {Array.isArray(licenses) ? licenses.length : 0} items
                </span>
              </div>
              <div className="d-flex gap-3 align-items-center">
                <div className="position-relative">
                  <input
                    type="search"
                    placeholder="Search licenses..."
                    className="form-input"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{ width: "200px" }}
                  />
                  {searchTerm === "" && (
                    <div
                      className="position-absolute"
                      style={{
                        top: "50%",
                        right: "13px",
                        color: "#666",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <FaSearch />
                    </div>
                  )}
                </div>
                <Button
                  variant="primary"
                  className="d-flex align-items-center gap-2 add-btn"
                  onClick={() => setShowCreateForm(true)}
                >
                  <FaPlus /> Create License Key
                </Button>
              </div>
            </div>

            {showCreateForm && (
              <Form onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="mb-3 p-3"
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <Row>
                    <Col md={8}>
                      <div className="form-group">
                        <label className="mb-1">
                          License Name <span className="text-danger">*</span>
                        </label>
                        <input
                          {...register("licenseName", {
                            required: "License name is required",
                            validate: (value) =>
                              value.trim() !== "" || "License name is required",
                          })}
                          className={`form-input ${
                            errors.licenseName ? "error" : ""
                          }`}
                          placeholder="Enter license name..."
                        />
                        <div>
                          {errors.licenseName && (
                            <span className="error-text">
                              {errors.licenseName.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="d-flex align-items-start">
                      <div
                        className="d-flex gap-2"
                        style={{ marginTop: "20px" }}
                      >
                        <button type="submit" className="add-btn">
                          Generate
                        </button>
                        <button
                          type="button"
                          className="cancel-btn-deleteModal"
                          onClick={() => {
                            setShowCreateForm(false);
                            reset();
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Form>
            )}

            {showEditForm && editingLicense && (
              <Form onSubmit={handleSubmitEdit(onUpdateSubmit)}>
                <div
                  className="mb-3 p-3"
                  style={{
                    background: "#e9ecef",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <Row>
                    <Col md={8}>
                      <div className="form-group">
                        <label className="mb-1">
                          Edit License Name{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          {...registerEdit("editLicenseName", {
                            required: "License name is required",
                            validate: (value) =>
                              value.trim() !== "" || "License name is required",
                          })}
                          className={`form-input ${
                            errorsEdit.editLicenseName ? "error" : ""
                          }`}
                          placeholder="Enter license name..."
                        />
                        <div>
                          {errorsEdit.editLicenseName && (
                            <span className="error-text">
                              {errorsEdit.editLicenseName.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="d-flex align-items-start">
                      <div
                        className="d-flex gap-2"
                        style={{ marginTop: "20px" }}
                      >
                        <button type="submit" className="add-btn">
                          Update
                        </button>
                        <button
                          type="button"
                          className="cancel-btn-deleteModal"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Form>
            )}

            <div className="table-scroll-wrapper">
              <table className="partners-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>License Name</th>
                    <th>License Key</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Days Left</th>
                    <th>IT Sales Partner</th>
                    <th>Allocate</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="11"
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#666",
                        }}
                      >
                        <div className="loading-spinner">
                          <div className="spinner"></div>
                          <p>Loading...</p>
                        </div>
                      </td>
                    </tr>
                  ) : !Array.isArray(licenses) || licenses.length === 0 ? (
                    <tr>
                      <td
                        colSpan="11"
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#666",
                        }}
                      >
                        No license keys available
                      </td>
                    </tr>
                  ) : (
                    Array.isArray(licenses) &&
                    licenses.map((license, index) => (
                      <tr
                        key={license._id}
                        className="table-row-hover"
                        onClick={() => handleTableRowClick(license)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{index + 1}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="coupon-icon me-2">
                              <FaKey className="text-primary" />
                            </div>
                            <div>
                              <div className="fw-bold text-primary">
                                {license.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <code
                              style={{
                                background: "#f8f9fa",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                border: "1px solid #e9ecef",
                              }}
                            >
                              {showKeys[license._id]
                                ? license.licenseKey
                                : maskKey(license.licenseKey)}
                            </code>
                            {showKeys[license._id] ? (
                              <FaEyeSlash
                                style={{
                                  color: "#007bff",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  padding: "2px",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleKeyVisibility(license._id);
                                }}
                                title="Hide key"
                              />
                            ) : (
                              <FaEye
                                style={{
                                  color: "#007bff",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  padding: "2px",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleKeyVisibility(license._id);
                                }}
                                title="Show key"
                              />
                            )}
                            <MdContentCopy
                              style={{
                                color:
                                  copiedId === license._id
                                    ? "#28a745"
                                    : "#007bff",
                                cursor: "pointer",
                                fontSize: "16px",
                                padding: "2px",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(
                                  license.licenseKey,
                                  license._id
                                );
                              }}
                              title={
                                copiedId === license._id
                                  ? "Copied!"
                                  : "Copy to clipboard"
                              }
                            />
                          </div>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Form.Switch
                            className={
                              license?.status === "active"
                                ? "custom-active info_pointer"
                                : ""
                            }
                            checked={license?.status === "active"}
                            onChange={() => handleStatusChange(license)}
                          />
                        </td>
                        <td>
                          <span className="text-muted">
                            {new Date(license.createdAt).toLocaleDateString()}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {license.startDate
                              ? new Date(license.startDate).toLocaleDateString()
                              : "---"}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {license.endDate
                              ? new Date(license.endDate).toLocaleDateString()
                              : "---"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`text-muted ${
                              license.daysBetween
                                ? license.daysBetween <= 30
                                  ? "text-danger"
                                  : license.daysBetween <= 60
                                  ? "text-warning"
                                  : "text-success"
                                : ""
                            }`}
                          >
                            {license.daysBetween
                              ? `${license.daysBetween} days`
                              : "---"}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {license.itsalesPartner ? (
                              <div>
                                <div
                                  className="fw-bold text-primary"
                                  style={{ fontSize: "12px" }}
                                >
                                  {license.itsalesPartner.companyName}
                                </div>
                                <small className="text-muted">
                                  {license.itsalesPartner.email}
                                </small>
                              </div>
                            ) : (
                              <span className="text-muted">---</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted">
                              {license.assigned
                                ? "Allocated"
                                : "Not Allocated Yet"}
                            </span>
                          </div>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex gap-1">
                            <div
                              className="edit-row-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditLicense(license);
                              }}
                              title="Edit"
                              style={{
                                cursor: "pointer",
                                padding: "4px",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <MdModeEditOutline />
                            </div>
                            <div
                              className="delete-row-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLicense(license);
                              }}
                              title="Delete"
                              style={{
                                cursor: "pointer",
                                padding: "4px",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <MdDelete />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages || 1}
              totalItems={pagination.totalItems || 0}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        licenseName={selectedLicense?.name}
      />

      <StatusConfirmModal
        isOpen={statusModal.isOpen}
        onClose={handleStatusCancel}
        onConfirm={handleStatusConfirm}
        licenseName={statusModal.license?.name}
        currentStatus={statusModal.license?.status}
        newStatus={statusModal.newStatus}
      />

      <LicenseUsageModal
        isOpen={licenseUsageOffcanvas.isOpen}
        onClose={handleUsageOffcanvasClose}
        license={licenseUsageOffcanvas.license}
      />
    </>
  );
};

export default Setting;
